import React from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { HandHeart, HeartHandshake, MapPinned, ChevronRight } from "lucide-react";

// One-time post-onboarding orientation (2026-02). Rides the celebration
// queue as kind "welcome" — server puts it FIRST and it's acked on display
// like every other one-shot, so it can never re-fire. No confetti/motion:
// reduced-motion safe by construction.
const WAYS = [
  {
    id: "give", icon: HandHeart, accent: "text-terracotta", bg: "bg-terracotta/10",
    title: "Give help",
    line: "Browse verified opportunities near you and start earning recognition.",
    to: "/opportunities", cta: "Find opportunities",
  },
  {
    id: "get", icon: HeartHandshake, accent: "text-sky-700", bg: "bg-sky-100",
    title: "Get help",
    line: "Shopping, a lift, a hand with the garden — ask your neighbours.",
    to: "/help-request/new", cta: "Ask your neighbours",
  },
  {
    id: "community", icon: MapPinned, accent: "text-forest", bg: "bg-forest/10",
    title: "Community",
    line: "Collectives, local events and stories from people near you.",
    to: "/collectives", cta: "Explore collectives",
  },
];

export function WelcomeOrientation({ user, onClose }) {
  const firstName = (user?.name || "").split(" ")[0];
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="welcome-orientation-dialog" className="sm:max-w-lg">
        <DialogHeader>
          <div className="text-[11px] uppercase tracking-[0.2em] text-terracotta font-bold">Welcome</div>
          <DialogTitle className="font-display text-3xl text-forest tracking-tight">
            {firstName ? `Welcome, ${firstName}!` : "Welcome to CivicHearth!"}
          </DialogTitle>
          <DialogDescription>
            You're all set up. There are three ways in — pick whatever feels right today.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {WAYS.map((w) => (
            <div key={w.id} className="flex items-center gap-4 bg-cream/60 border border-forest/10 rounded-xl p-4">
              <div className={`w-10 h-10 rounded-full ${w.bg} flex items-center justify-center shrink-0`}>
                <w.icon className={`w-5 h-5 ${w.accent}`} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-medium text-forest text-sm">{w.title}</div>
                <div className="text-xs text-neutral-600 mt-0.5">{w.line}</div>
              </div>
              <Link to={w.to} onClick={onClose} data-testid={`welcome-way-${w.id}`}
                    className="shrink-0 inline-flex items-center gap-1 text-xs font-medium text-forest hover:underline">
                {w.cta} <ChevronRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          ))}
        </div>
        <div className="flex justify-end">
          <Button data-testid="welcome-dismiss-btn" variant="outline" onClick={onClose} className="rounded-full">
            Explore on my own
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
